import React, { useEffect, useState } from "react";
import {
  Flex,
  Box,
  Text,
  useColorModeValue,
  Spinner,
  List,
  ListItem,
  Button,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
} from "@chakra-ui/react";
import ProductApi from "api/product";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

const listEmoji = "🎯";
const headerEmojis = ["🎮", "🔥", "✨", "⚡", "💥", "🌟", "🚀", "🎉"];
const getRandomHeaderEmoji = () => {
  const index = Math.floor(Math.random() * headerEmojis.length);
  return headerEmojis[index];
};

const FreeZ = () => {
  const [data, setData] = useState({
    ps4Names: [],
    ps5Names: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeZType, setActiveZType] = useState(1);
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();

  const fetchProductNames = async (zType) => {
    setLoading(true);
    try {
      const response = await ProductApi.FreeZ(zType);

      const formatNameWithPrice = (product) => {
        const price = product.attributes?.[0]?.price ?? 0;
        const formattedPrice = Math.round(price / 1000);
        return `${product.name} - ${formattedPrice}T`;
      };

      const sortedPs4Names = response.data.free_z_ps4
        .map(formatNameWithPrice)
        .sort();

      const sortedPs5Names = response.data.free_z_ps5
        .map(formatNameWithPrice)
        .sort();

      setData({ ps4Names: sortedPs4Names, ps5Names: sortedPs5Names });
      setError(null);
    } catch (err) {
      setError("خطا در دریافت داده‌ها");
      console.error("Error fetching product names:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductNames(activeZType);
  }, [activeZType]);

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        toast({
          title: "کپی انجام شد!",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      }).catch((err) => {
        console.error("Failed to copy text: ", err);
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
      document.body.prepend(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast({
          title: "کپی انجام شد!",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      } catch (error) {
        console.error(error);
      } finally {
        textArea.remove();
      }
    }
  };

  const handleCopy = (list, type) => {
    const emoji = getRandomHeaderEmoji();
    const header = `${emoji} لیست ظرفیت ${activeZType} بازی‌های ${type} ${emoji}\n\n`;
    const text =
      header + list.map((name) => `${listEmoji} ${name}`).join("\n");
    copyToClipboard(text);
  };

  if (loading)
    return (
      <Flex justify="center" align="center" minH="300px">
        <Spinner size="xl" />
      </Flex>
    );

  if (error)
    return (
      <Flex justify="center" align="center" minH="300px">
        <Text color="red.500" fontWeight="bold">
          {error}
        </Text>
      </Flex>
    );

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} px={{ base: 4, md: 12 }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }} boxShadow="lg" borderRadius="md">
        <CardHeader
          p="6px 0 22px 0"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Text fontSize={{ base: "lg", md: "xl" }} color={textColor} fontWeight="bold">
            لیست بازی‌های ظرفیت {activeZType}
          </Text>
        </CardHeader>
        <CardBody>
          <Tabs
            w="100%"
            variant="enclosed"
            index={activeZType - 1}
            onChange={(index) => setActiveZType(index + 1)}
          >
            <TabList borderBottomColor="gray.300">
              {[1, 2, 3].map((zType) => (
                <Tab key={zType} fontWeight="bold" fontSize="md" _selected={{ color: "orange.400" }}>
                  ظرفیت {zType}
                </Tab>
              ))}
            </TabList>
            <TabPanels mt={6}>
              {[1, 2, 3].map((zType) => (
                <TabPanel key={zType} px={0}>
                  <Flex direction={{ base: "column", md: "row" }} gap={6}>
                    <Box
                      flex="1"
                      p={4}
                      borderLeft={{ base: "none", md: "4px solid" }}
                      borderColor="orange.300"
                      borderRadius="md"
                      bg={useColorModeValue("gray.50", "gray.700")}
                      boxShadow="sm"
                    >
                      <Button
                        colorScheme="orange"
                        mb={4}
                        size="sm"
                        w="100%"
                        onClick={() => handleCopy(data.ps4Names, "PS4")}
                      >
                        کپی لیست PS4
                      </Button>
                      <Divider mb={3} />
                      <Text fontSize="lg" fontWeight="bold" mb={3} textAlign="left" color="orange.400">
                        لیست ظرفیت {zType} PS4
                      </Text>
                      <List
                        spacing={2}
                        textAlign="left"
                        styleType="none"
                        maxH="300px"
                        overflowY="auto"
                        sx={{ direction: "ltr" }}
                      >
                        {data.ps4Names.length > 0 ? (
                          data.ps4Names.map((name, index) => (
                            <ListItem
                              key={index}
                              color={textColor}
                              fontWeight="medium"
                              fontSize="md"
                              display="flex"
                              alignItems="center"
                              gap={2}
                            >
                              <Text>{listEmoji}</Text>
                              <Text>{name}</Text>
                            </ListItem>
                          ))
                        ) : (
                          <Text color="gray.500" fontStyle="italic">
                            محصولی برای PS4 وجود ندارد
                          </Text>
                        )}
                      </List>
                    </Box>
                    <Box
                      flex="1"
                      p={4}
                      borderLeft={{ base: "none", md: "4px solid" }}
                      borderColor="teal.400"
                      borderRadius="md"
                      bg={useColorModeValue("gray.50", "gray.700")}
                      boxShadow="sm"
                    >
                      <Button
                        colorScheme="teal"
                        mb={4}
                        size="sm"
                        w="100%"
                        onClick={() => handleCopy(data.ps5Names, "PS5")}
                      >
                        کپی لیست PS5
                      </Button>
                      <Divider mb={3} />
                      <Text fontSize="lg" fontWeight="bold" mb={3} textAlign="left" color="teal.400">
                        لیست ظرفیت {zType} PS5
                      </Text>
                      <List
                        spacing={2}
                        textAlign="left"
                        styleType="none"
                        maxH="300px"
                        overflowY="auto"
                        sx={{ direction: "ltr" }}
                      >
                        {data.ps5Names.length > 0 ? (
                          data.ps5Names.map((name, index) => (
                            <ListItem
                              key={index}
                              color={textColor}
                              fontWeight="medium"
                              fontSize="md"
                              display="flex"
                              alignItems="center"
                              gap={2}
                            >
                              <Text>{listEmoji}</Text>
                              <Text>{name}</Text>
                            </ListItem>
                          ))
                        ) : (
                          <Text color="gray.500" fontStyle="italic">
                            محصولی برای PS5 وجود ندارد
                          </Text>
                        )}
                      </List>
                    </Box>
                  </Flex>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
    </Flex>
  );
};

export default FreeZ;
